import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { IDailyAccruedInterest } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface IGetDailyAccruedInterestResponse {
  status?: boolean;
  message?: string;
  data?: IDailyAccruedInterest[];
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetDailyAccruedInterestResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getDailyAccruedInterestAsync = createAsyncThunk(
  'savings/interest/dailyAccrued',
  async (savingsAccountNumber: string, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.get<IGetDailyAccruedInterestResponse>(
        `/savings/interest-payments/daily-accrued-interest/${savingsAccountNumber}`,
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const dailyAccruedInterestSlice = createSlice({
  name: 'savings/interest/dailyAccrued',
  initialState,
  reducers: {
    resetDailyAccruedInterest(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDailyAccruedInterestAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getDailyAccruedInterestAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetDailyAccruedInterestResponse;
      })
      .addCase(getDailyAccruedInterestAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetDailyAccruedInterest } = dailyAccruedInterestSlice.actions;

export default dailyAccruedInterestSlice.reducer;
